import { reactive, ref } from 'vue';
import uploadService from '@/Services/uploadService';
import logger from '@/Utils/logger';
import { UploadParentItemType } from '@/Types/Enums/UploadParentItemType';
import BaseUploadModel from '@/Models/baseUploadModel';
import {
  groupUploadsToFolders, isUploadBelongsToFolder
} from '@/VueComponents/Uploads/utils/uploadsUtils';
import Upload from '@/Types/upload';
import UploadModel from '@/Models/uploadModel';
import { useUploadManagerStore } from '@/VueCore/stores/uploadManagerStore';
import UploadManager from '@/VueCore/stores/Models/uploadManager';

export default function useUploadLoader(requestId: string,
  requestType: UploadParentItemType,
  uploadsArray?: BaseUploadModel[]) {
  const uploads = reactive<BaseUploadModel[]>(uploadsArray ?? []);
  const loading = ref<boolean>(false);
  const loadingFailed = ref<boolean>(false);

  function load() {
    loading.value = true;
    loadingFailed.value = false;

    const loadUploadsFunction = requestType === UploadParentItemType.selfResponse ?
      uploadService.getUploadsByResponseId : uploadService.getUploadsByRequestId;

    loadUploadsFunction(requestId).then((data: Upload[]) => {
      uploads.length = 0;

      if (!data?.length) {
        return;
      }

      const uploadManager = useUploadManagerStore() as UploadManager;
      const uploadModels = [];
      for (const upload of data) {
        const uploadModel = getUploadModelFromUploadManagerOrCreate(upload, uploadManager);
        uploadModels.push(uploadModel);
      }

      const folderUploads = uploadModels.filter(isUploadBelongsToFolder);

      const folders = groupUploadsToFolders(folderUploads);
      const files = uploadModels.filter(u => !isUploadBelongsToFolder(u));


      if (folders.length > 0) {
        uploads.push(...folders);
      }
      if (files.length > 0) {
        uploads.push(...files);
      }
    }).catch(e => {
      loadingFailed.value = true;
      logger.error('FailedToRetrieveUploads');
    }).finally(() => {
      loading.value = false;
    });

    function getUploadModelFromUploadManagerOrCreate(upload: Upload, uploadManager: UploadManager): UploadModel {
      const existingUpload = uploadManager.getUploadById(upload.uploadId);
      return existingUpload ?? UploadModel.fromServerUpload(upload);
    }

    return { load, loading, loadingFailed, uploads };
  }

  return { load, uploads, loading, loadingFailed };
}
